import { useState, useEffect, useCallback } from 'react';
import { useDateFormat } from './DateFormatContext';
import { useNavigate } from 'react-router-dom';
import Icon from './Icons';
import { usePullToRefresh } from './hooks';
import ErrorScreen from './ErrorScreen';
import { formatSubstrate, substrateComponents } from './GrowingMethodPicker';
import { formatStatus } from './statuses';

// ─── Navigate to plant list with a pre-set filter ──────────────────────────
// Resets ALL existing filters first so Stats taps always produce a clean view.
// Sets filtersOpen: false — the panel stays closed until user opens it.
function useNavigateWithFilter() {
  const navigate = useNavigate();
  return useCallback((filterPatch) => {
    // Save Stats scroll position so back-navigation restores it
    sessionStorage.setItem('statsScroll', String(window.scrollY));
    sessionStorage.setItem('plantListState', JSON.stringify({
      search: '', sortField: 'name', sortDir: 'asc',
      filtersOpen: false,
      statusFilter:   [],
      methodFilter:   [],
      locationFilter: [],
      lightFilter:    [],
      genusFilter:    [],
      substrateFilter:[],
      ...filterPatch,
    }));
    navigate('/');
  }, [navigate]);
}

// ─── Stat Card ─────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, accent = false }) {
  return (
    <div
      className="stat-card"
      style={{ borderLeftColor: accent ? 'var(--green)' : 'var(--border)' }}
    >
      <div className="stat-card-label">{label}</div>
      <div
        className="stat-card-value"
        style={{ color: accent ? 'var(--green)' : 'var(--text)' }}
      >
        {value}
      </div>
      {sub && <div className="stat-card-sub">{sub}</div>}
    </div>
  );
}

// ─── Mini Bar ──────────────────────────────────────────────────────────────

function formatPercent(count, total) {
  if (!total) return null;
  const pct = (count / total) * 100;
  if (pct > 0 && pct < 1) return '<1%';
  return `${Math.round(pct)}%`;
}

function barWidth(count, total) {
  if (!total) return 0;
  const pct = (count / total) * 100;
  return count > 0 ? Math.max(pct, 4) : 0;
}

function MiniBar({ label, count, total, color, showPercent = false }) {
  const pct = barWidth(count, total);
  const share = showPercent ? formatPercent(count, total) : null;
  return (
    <div className="mini-bar">
      <div className="mini-bar-header">
        <span className="mini-bar-label">{label}</span>
        <span className="mini-bar-count">{count}{share && <span className="mini-bar-share"> {share}</span>}</span>
      </div>
      <div className="mini-bar-track">
        <div
          className="mini-bar-fill"
          style={{ width: `${pct}%`, background: color || 'var(--green)' }}
        />
      </div>
    </div>
  );
}

// Tappable variant — navigates to filtered plant list on click
function TappableMiniBar({ label, count, total, color, onClick, showPercent = true }) {
  const pct = barWidth(count, total);
  const share = showPercent ? formatPercent(count, total) : null;
  return (
    <div
      className="mini-bar mini-bar--tappable"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onClick?.()}
    >
      <div className="mini-bar-header">
        <span className="mini-bar-label">{label}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          <span className="mini-bar-count">{count}{share && <span className="mini-bar-share"> {share}</span>}</span>
          <Icon name="arrowRight" size={11} style={{ color: 'var(--text-xlight)', flexShrink: 0 }} />
        </div>
      </div>
      <div className="mini-bar-track">
        <div
          className="mini-bar-fill"
          style={{ width: `${pct}%`, background: color || 'var(--green)' }}
        />
      </div>
    </div>
  );
}

function CompositionBar({ rows, total, getLabel, getColor, maxSegments = 7 }) {
  const visible = rows.filter(row => row.count > 0);
  if (!total || visible.length < 2 || visible.length > maxSegments) return null;

  return (
    <div className="composition-bar" aria-label="Composition by percent">
      <div className="composition-strip">
        {visible.map((row, i) => {
          const pct = (row.count / total) * 100;
          return (
            <div
              key={`${getLabel(row)}-${i}`}
              className="composition-segment"
              title={`${getLabel(row)}: ${row.count} (${formatPercent(row.count, total)})`}
              style={{
                width: `${pct}%`,
                background: getColor?.(row, i) || 'var(--green)',
              }}
            />
          );
        })}
      </div>
      <div className="composition-legend">
        {visible.map((row, i) => (
          <span key={`${getLabel(row)}-legend-${i}`} className="composition-legend-item">
            <span
              className="composition-swatch"
              style={{ background: getColor?.(row, i) || 'var(--green)' }}
            />
            <span className="composition-legend-label">{getLabel(row)}</span>
            <span className="composition-legend-value">{formatPercent(row.count, total)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

// Genera with count at or below this threshold collapse into "Other"
const GENUS_OTHER_THRESHOLD = 2;

function Sparkline({ data }) {
  if (!data || data.length < 2) return null;

  const counts = data.map(d => d.count);
  const max = Math.max(...counts);
  const min = Math.min(...counts);
  const range = max - min || 1;
  const W = 280, H = 44;

  const points = counts.map((v, i) => {
    const x = (i / (counts.length - 1)) * W;
    const y = H - 6 - ((v - min) / range) * (H - 12);
    return `${x},${y}`;
  }).join(' ');

  const lastPt = points.split(' ').pop().split(',');

  return (
    <svg
      width="100%"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      style={{ display: 'block', overflow: 'visible' }}
    >
      <polyline
        points={points}
        fill="none"
        stroke="var(--green)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={lastPt[0]} cy={lastPt[1]} r="3.5" fill="var(--green)" />
    </svg>
  );
}

// ─── Section Card ──────────────────────────────────────────────────────────

function SectionCard({ title, children }) {
  return (
    <div className="stats-section-card">
      <h2 className="stats-section-title">{title}</h2>
      {children}
    </div>
  );
}

// ─── Callout Row (tappable — navigates to plant detail) ────────────────────

function CalloutRow({ label, displayName, specimenId, plantId, meta, onClick }) {
  return (
    <div className="stats-callout-row" onClick={() => onClick(plantId)}>
      <div className="stats-callout-label">{label}</div>
      <div className="stats-callout-name">{displayName}</div>
      <div className="stats-callout-meta">
        <span className="specimen-id">{specimenId}</span>
        {meta && <span className="stats-callout-meta-text">{meta}</span>}
      </div>
    </div>
  );
}

function CareRoundsPanel({ onStart }) {
  return (
    <button className="stats-care-panel" onClick={onStart}>
      <span className="stats-care-icon">
        <Icon name="activity" size={20} />
      </span>
      <span className="stats-care-copy">
        <span className="stats-care-label">Care Rounds</span>
        <span className="stats-care-title">Start a guided check-in</span>
        <span className="stats-care-meta">Overdue, location, method, tag, or surprise batches</span>
      </span>
      <span className="stats-care-cta">
        Start <Icon name="arrowRight" size={14} />
      </span>
    </button>
  );
}

// ─── Stats Screen ──────────────────────────────────────────────────────────

export default function Stats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [genusExpanded, setGenusExpanded] = useState(false);
  const [gapCount, setGapCount] = useState(0);
  const navigate = useNavigate();
  const navigateWithFilter = useNavigateWithFilter();
  const { dateFmt } = useDateFormat();

  // Restore scroll position when returning from plant list
  useEffect(() => {
    const saved = sessionStorage.getItem('statsScroll');
    if (saved) {
      requestAnimationFrame(() => window.scrollTo(0, parseInt(saved)));
      sessionStorage.removeItem('statsScroll');
    }
  }, []);

  const fetchStats = useCallback(() => {
    setLoading(true);
    const statsP = fetch('/api/stats')
      .then(r => { if (!r.ok) throw new Error('Failed to fetch stats'); return r.json(); })
      .then(data => { setStats(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });

    fetch('/plants/gaps')
      .then(r => r.ok ? r.json() : [])
      .then(gaps => {
        const meaningful = gaps.filter(p =>
          p.gaps.some(g => g.priority === 'must' || g.priority === 'nice')
        );
        setGapCount(meaningful.length);
      })
      .catch(() => {});

    return statsP;
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const refreshing = usePullToRefresh(fetchStats);

  if (loading) return <div className="app"><p>Loading stats...</p></div>;
  if (error)   return <div className="app"><ErrorScreen message={error} onRetry={fetchStats} /></div>;

  const { collection, propagation, financial, coverage, monthly_acquisitions, activity } = stats;

  const statusRows = Object.entries(collection.by_status).sort((a, b) => b[1] - a[1]);
  const statusByKey = Object.fromEntries((collection.statuses || []).map(status => [status.key, status]));
  const statusColor = status => statusByKey[status]?.color_fg || 'var(--green)';

  const COMPOSITION_COLORS = [
    'var(--green)',
    '#004085',
    '#6a2d8a',
    '#856404',
    '#0c5460',
    '#8a2d2d',
    'var(--text-light)',
  ];

  const netInvestment = financial.total_cost != null
    ? (financial.total_cost - (financial.total_revenue || 0)).toFixed(2)
    : null;

  const monthLabels = monthly_acquisitions.map(d => {
    const [y, m] = d.month.split('-');
    return new Date(y, m - 1).toLocaleString('default', { month: 'short' });
  });

  function formatMonthYear(dateStr) {
    if (!dateStr) return null;
    const d = new Date(dateStr + 'T00:00:00');
    if (dateFmt === 'iso') {
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    }
    const locale = dateFmt === 'dmy' ? 'en-GB' : dateFmt === 'mdy' ? 'en-US' : undefined;
    return d.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  }

  function formatRelativeDate(dateStr) {
    if (!dateStr) return null;
    const d = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00Z');
    const days = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (days === 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 7)  return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
    return `${Math.floor(days / 30)} months ago`;
  }

  return (
    <div className="app">
      {refreshing && (
        <div className="plan-ptr-indicator">
          <Icon name="rotateCw" size={16} /> Refreshing…
        </div>
      )}

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <header className="app-header">
        <div className="app-header-brand">
          <img src="/logo_only.svg" alt="Aroidex" className="app-header-logo" />
          <div>
            <h1>Stats</h1>
            <p className="plant-count">{collection.total} plants</p>
          </div>
        </div>
      </header>

      {/* ── Collection Overview ─────────────────────────────────────────── */}
      <SectionCard title="Collection">
        <div className="stat-card-grid">
          <StatCard label="Total Plants"  value={collection.total}                        sub="active collection" accent />
          <StatCard label="Genera"        value={collection.by_genus.length}              sub="represented" />
          <StatCard label="Propagation"   value={collection.by_status['propagation'] || 0} sub="in progress" />
          {collection.wishlist > 0 && (
            <StatCard label="Wishlist" value={collection.wishlist} sub="not in collection" />
          )}
        </div>
      </SectionCard>

      <CareRoundsPanel
        onStart={() => { sessionStorage.setItem('statsScroll', String(window.scrollY)); navigate('/rounds'); }}
      />

      {gapCount >= 5 && (
        <div className="health-banner stats-gap-banner">
          <div className="health-banner-body">
            <Icon name="clipboardCheck" size={16} className="health-banner-icon" />
            <div>
              <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text)' }}>
                <strong>{gapCount} plants</strong> need data cleanup
              </p>
              <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-light)' }}>
                Fill priority gaps to make the coverage metrics more trustworthy
              </p>
            </div>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/health')}>
            Fill gaps →
          </button>
        </div>
      )}

      {/* ── Collection Health ───────────────────────────────────────────── */}
      {(activity.last_observation || activity.veteran || activity.overdue?.length > 0) && (
        <SectionCard title="Collection Health">
          {activity.last_observation && (
            <CalloutRow
              label="Last tended"
              displayName={activity.last_observation.display_name}
              specimenId={activity.last_observation.specimen_id}
              plantId={activity.last_observation.plant_id}
              meta={formatRelativeDate(activity.last_observation.date)}
              onClick={id => { sessionStorage.setItem('statsScroll', String(window.scrollY)); navigate(`/plants/${id}`, { state: { returnTo: '/stats' } }); }}
            />
          )}
          {activity.veteran && (
            <CalloutRow
              label="Longest held"
              displayName={activity.veteran.display_name}
              specimenId={activity.veteran.specimen_id}
              plantId={activity.veteran.plant_id}
              meta={`Since ${formatMonthYear(activity.veteran.acquisition_date)}`}
              onClick={id => { sessionStorage.setItem('statsScroll', String(window.scrollY)); navigate(`/plants/${id}`, { state: { returnTo: '/stats' } }); }}
            />
          )}
          {activity.overdue?.length > 0 && (
            <>
              <div className="stats-subsection-label" style={{ marginTop: '0.75rem' }}>
                Overdue for check-in
              </div>
              {activity.overdue.map(plant => (
                <CalloutRow
                  key={plant.plant_id}
                  label={`${plant.days_since}d ago`}
                  displayName={plant.display_name}
                  specimenId={plant.specimen_id}
                  plantId={plant.plant_id}
                  meta="no recent activity"
                  onClick={id => { sessionStorage.setItem('statsScroll', String(window.scrollY)); navigate(`/plants/${id}`, { state: { returnTo: '/stats' } }); }}
                />
              ))}
            </>
          )}
        </SectionCard>
      )}

      {/* ── Most Active Plants ──────────────────────────────────────────── */}
      {activity.most_active.length > 0 && (
        <SectionCard title="Most Active Plants">
          {activity.most_active.map((plant, i) => (
            <CalloutRow
              key={plant.plant_id}
              label={`#${i + 1}`}
              displayName={plant.display_name}
              specimenId={plant.specimen_id}
              plantId={plant.plant_id}
              meta={`${plant.obs_count} observations`}
              onClick={id => navigate(`/plants/${id}`, { state: { returnTo: '/stats' } })}
            />
          ))}
        </SectionCard>
      )}

      {/* ── Status Breakdown ────────────────────────────────────────────── */}
      {statusRows.length > 1 && (
        <SectionCard title="By Status">
          <CompositionBar
            rows={statusRows.map(([status, count]) => ({ status, count }))}
            total={collection.total}
            getLabel={row => formatStatus(row.status, statusByKey)}
            getColor={row => statusColor(row.status)}
          />
          {statusRows.map(([status, count]) => (
            <MiniBar
              key={status}
              label={formatStatus(status, statusByKey)}
              count={count}
              total={collection.total}
              color={statusColor(status)}
              showPercent
            />
          ))}
        </SectionCard>
      )}

      {/* ── Genus Breakdown ─────────────────────────────────────────────── */}
      {collection.by_genus.length > 0 && (() => {
        const main  = collection.by_genus.filter(g => g.count > GENUS_OTHER_THRESHOLD);
        const other = collection.by_genus.filter(g => g.count <= GENUS_OTHER_THRESHOLD);
        const otherCount = other.reduce((s, g) => s + g.count, 0);
        const shown = genusExpanded ? collection.by_genus : main;
        return (
          <SectionCard title="By Genus">
            {shown.map(({ genus, count }) => (
              <TappableMiniBar
                key={genus}
                label={genus}
                count={count}
                total={collection.total}
                onClick={() => navigateWithFilter({ genusFilter: [genus] })}
              />
            ))}
            {!genusExpanded && other.length > 0 && (
              <button className="stats-other-toggle" onClick={() => setGenusExpanded(true)}>
                + {other.length} more genera ({otherCount} plants)
              </button>
            )}
            {genusExpanded && other.length > 0 && (
              <button className="stats-other-toggle" onClick={() => setGenusExpanded(false)}>
                Show less
              </button>
            )}
          </SectionCard>
        );
      })()}

      {/* ── Location Breakdown ──────────────────────────────────────────── */}
      {collection.by_location.length > 0 && (
        <SectionCard title="By Location">
          <CompositionBar
            rows={collection.by_location}
            total={collection.total}
            getLabel={row => row.location}
            getColor={(_, i) => COMPOSITION_COLORS[i % COMPOSITION_COLORS.length]}
          />
          {collection.by_location.map(({ location, count, location_id }) => (
            <TappableMiniBar
              key={location}
              label={location}
              count={count}
              total={collection.total}
              color="#004085"
              onClick={() => navigateWithFilter({ locationFilter: [location_id] })}
            />
          ))}
        </SectionCard>
      )}

      {/* ── Growing Setup (method + substrate grouped) ───────────────────── */}
      {(collection.by_method.length > 0 || collection.by_substrate.length > 0) && (
        <SectionCard title="Growing Setup">
          {collection.by_method.length > 0 && (
            <>
              <div className="stats-subsection-label">Method</div>
              <CompositionBar
                rows={collection.by_method}
                total={collection.total}
                getLabel={row => row.method}
                getColor={(_, i) => COMPOSITION_COLORS[i % COMPOSITION_COLORS.length]}
              />
              {collection.by_method.map(({ method, count }) => (
                <TappableMiniBar
                  key={method}
                  label={method}
                  count={count}
                  total={collection.total}
                  color="var(--green-dark)"
                  onClick={() => navigateWithFilter({ methodFilter: [method] })}
                />
              ))}
            </>
          )}
          {collection.by_substrate.length > 0 && (
            <>
              <div className="stats-subsection-label" style={{ marginTop: '0.75rem' }}>Substrate</div>
              <CompositionBar
                rows={collection.by_substrate}
                total={collection.total}
                getLabel={row => formatSubstrate(row.substrate)}
                getColor={(_, i) => COMPOSITION_COLORS[i % COMPOSITION_COLORS.length]}
              />
              {collection.by_substrate.map(({ substrate, count }) => (
                <TappableMiniBar
                  key={substrate}
                  label={formatSubstrate(substrate)}
                  count={count}
                  total={collection.total}
                  color="var(--green-dark)"
                  onClick={() => navigateWithFilter({ substrateFilter: substrateComponents(substrate) })}
                />
              ))}
            </>
          )}
        </SectionCard>
      )}

      {/* ── Propagation ─────────────────────────────────────────────────── */}
      <SectionCard title="Propagation">
        <div className="stat-card-grid">
          <StatCard label="Attempts"    value={propagation.total}      sub="logged" />
          <StatCard label="Successful"  value={propagation.successful} sub="live children" accent />
          {propagation.success_rate != null && (
            <StatCard label="Success Rate" value={`${propagation.success_rate}%`} sub="with live child" />
          )}
        </div>
        {propagation.by_method.length > 0 && (
          <div style={{ marginTop: '0.75rem' }}>
            <div className="stats-subsection-label">By Method</div>
            {propagation.by_method.map(({ method, count }) => (
              <MiniBar
                key={method}
                label={method.replace(/_/g, ' ')}
                count={count}
                total={propagation.total}
                color="#6a2d8a"
                showPercent
              />
            ))}
          </div>
        )}
        {propagation.total_items > 0 && (
          <>
            <div className="stats-subsection-label" style={{ marginTop: '1rem' }}>Batch Items</div>
            <div className="stat-card-grid">
              <StatCard label="In Progress" value={propagation.active_items}    sub="active" />
              <StatCard label="Graduated"   value={propagation.graduated_items} sub="became plants" accent />
              <StatCard label="Failed"      value={propagation.failed_items}    sub="didn't make it" />
              {propagation.item_success_rate != null && (
                <StatCard label="Success Rate" value={`${propagation.item_success_rate}%`} sub="of resolved items" />
              )}
            </div>
            {propagation.items_by_failure_reason?.length > 0 && (
              <div style={{ marginTop: '0.75rem' }}>
                <div className="stats-subsection-label">Failure Reasons</div>
                {propagation.items_by_failure_reason.map(({ reason, count }) => (
                  <MiniBar
                    key={reason}
                    label={reason.replace(/_/g, ' ')}
                    count={count}
                    total={propagation.failed_items}
                    color="#c0392b"
                    showPercent
                  />
                ))}
              </div>
            )}
          </>
        )}
      </SectionCard>

      {/* ── Acquisitions Sparkline ──────────────────────────────────────── */}
      {monthly_acquisitions.length >= 2 && (
        <SectionCard title="Acquisitions (Last 12 Months)">
          <Sparkline data={monthly_acquisitions} />
          <div className="sparkline-labels">
            {monthLabels.map((m, i) => (
              <span key={i} className="sparkline-label">{m}</span>
            ))}
          </div>
        </SectionCard>
      )}

      {/* ── Financial Summary ───────────────────────────────────────────── */}
      {financial.plants_with_cost > 0 && (
        <SectionCard title="Financial">
          <div className="stat-card-grid">
            {financial.total_cost != null && (
              <StatCard
                label="Total Cost"
                value={`$${financial.total_cost.toFixed(2)}`}
                sub={`${financial.plants_with_cost} plants tracked`}
                accent
              />
            )}
            {financial.avg_cost != null && (
              <StatCard label="Avg Cost" value={`$${financial.avg_cost.toFixed(2)}`} sub="per plant" />
            )}
            {financial.total_sales > 0 && (
              <StatCard
                label="Sales Revenue"
                value={`$${(financial.total_revenue || 0).toFixed(2)}`}
                sub={`${financial.total_sales} sold`}
              />
            )}
            {netInvestment != null && (
              <StatCard label="Net Investment" value={`$${netInvestment}`} sub="cost minus revenue" />
            )}
          </div>
          {financial.plants_with_cost < coverage.total && (
            <p className="stats-coverage-note">
              Cost recorded for {financial.plants_with_cost} of {coverage.total} plants.
              Add cost in plant detail to improve accuracy.
            </p>
          )}
        </SectionCard>
      )}

      {/* ── Data Coverage ───────────────────────────────────────────────── */}
      <div className="stats-section-card">
        <div className="stats-section-title-row">
          <h2 className="stats-section-title" style={{ margin: 0, border: 'none', paddingBottom: 0 }}>
            Data Coverage
          </h2>
          <button
            className="btn btn-sm btn-ghost"
            onClick={() => navigate('/health')}
            style={{ whiteSpace: 'nowrap' }}
          >
            Fill gaps →
          </button>
        </div>
        {[
          { label: 'Plants with photos',           count: coverage.with_photos,   color: 'var(--green)' },
          { label: 'Plants with acquisition date',  count: coverage.with_acq_date, color: '#856404' },
          { label: 'Plants with cost recorded',     count: coverage.with_cost,     color: '#6a2d8a' },
          { label: 'Plants with lineage',           count: coverage.with_lineage,  color: '#004085' },
          { label: 'Plants with notes',             count: coverage.with_notes,    color: '#0c5460' },
        ].map(row => (
          <MiniBar key={row.label} {...row} total={coverage.total} />
        ))}
      </div>

      <div style={{ height: '1.5rem' }} />
    </div>
  );
}
